import * as React from 'react';

import {
  apply,
  compose,
  flatten,
  pluck,
  isEmpty,
  applySpec,
  clamp,
  splitAt,
  concat,
  head,
  last,
} from 'ramda';

import { number, string, func, shape, arrayOf } from 'prop-types';

import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryScatter,
  VictoryVoronoiContainer,
  VictoryGroup,
} from 'victory';

import dateFormat from 'date-fns/format';
import { range } from 'd3-array';
import { timeMonth, timeYear } from 'd3-time';

import { Precision as IPrecision } from '../../models';
import { Precision, VisType } from '../../constants';

import { NoChart } from '../NoContent';
import PortalTooltip from './PortalTooltip';

import { victoryTheme } from '../../theme';

const strokeWidthActive = 2.5;
const strokeWidthNormal = 1.5;

const margin = 24;
const chartPadding = {
  bottom: 36,
  top: margin + 8,
  left: margin + 36,
  right: margin,
};

const domainPadding = { x: [18, 0], y: [0, 0] };
const tickLabelSpacing = 100;
const tickLabelMinSpacing = 60;

export const getDataRange = (chartData, attr) =>
  compose(
    applySpec({ min: apply(Math.min), max: apply(Math.max) }),
    pluck(attr),
    flatten,
    pluck('points'),
  )(chartData);

// We add +1 to make end inclusive.
// Dates are expected to be numbers.
export const getAllTicks = ({ min, max }, precision) =>
  (precision === Precision.YEARLY ? timeYear : timeMonth)
    .range(min, max + 1)
    .map(d => d.getTime());

// We enforce last index to be = count - 1.
// Number of segments = count - 1.
// TODO: ticks logic needs to be explained
export const getTicksIndices = (count, width) => {
  const ticksStep = clamp(
    1,
    count - 1,
    Math.ceil((count - 1) * tickLabelSpacing / width),
  );

  // If we can fit all labels with minimal spacing, do it.
  if ((count - 1) * tickLabelMinSpacing < width) {
    return range(0, count, 1);
  }

  // Otherwise we have to decide, should we drop last tick after
  // including end of the interval to the set of labels.
  const [head, [lastTick]] = splitAt(-1, range(0, count, ticksStep));

  // If last segment has acceptable width, do not drop.
  const lastSegmentWidth = (count - 1 - lastTick) * width / (count - 1);
  if (lastSegmentWidth >= tickLabelMinSpacing) {
    return concat(head, [lastTick, count - 1]);
  }

  // Otherwise, drop.
  return concat(head, [count - 1]);
};

export class LineChart extends React.Component {
  state = {
    externalMutations: undefined,
  };

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedId === this.props.selectedId) return;

    let externalMutations = [];

    // deselect previous if present
    if (this.props.selectedId)
      externalMutations.push({
        childName: this.props.selectedId,
        target: 'data',
        eventKey: 'all',
        mutation: () => ({ active: false }),
        callback: () =>
          // will persist on mutating while chart is selected but will clear
          // mutation on deselection
          this.setState({ externalMutations: undefined }),
      });

    // select next id if present
    if (nextProps.selectedId !== null) {
      externalMutations.push({
        childName: nextProps.selectedId,
        target: 'data',
        eventKey: 'all',
        mutation: () => ({ active: true }),
      });
    }

    this.setState({ externalMutations });
  }

  render() {
    const {
      width,
      height,
      chartData,
      selectedId,
      hoveredId,
      setSelected,
      setHovered,
      precision,
    } = this.props;
    if (!chartData || isEmpty(chartData)) return <NoChart />;

    const allTicks = getAllTicks(getDataRange(chartData, 'x'), precision);

    const ticks = getTicksIndices(
      allTicks.length,
      width - chartPadding.left - chartPadding.right,
    ).map(id => allTicks[id]);

    return (
      <React.Fragment>
        <VictoryChart
          scale={{ x: 'time', y: 'linear' }}
          padding={chartPadding}
          style={{ parent: { background: 'white' } }}
          width={width}
          height={height}
          domainPadding={domainPadding}
          externalEventMutations={this.state.externalMutations}
          containerComponent={
            <VictoryVoronoiContainer
              labels={d => d.y}
              labelComponent={
                <PortalTooltip
                  visType={VisType.LINE}
                  width={width}
                  height={height}
                  node={this.portalNode}
                />
              }
              radius={20}
              // we’re doing separate scatter chart for points so we can ignore
              // line chart events
              voronoiBlacklist={[
                'line',
                // also ignore everything that’s not currently selected chart
                ...(selectedId
                  ? chartData
                      .map(({ id }) => id)
                      .filter(id => id !== selectedId)
                  : []),
              ]}
              onActivated={([point]) => {
                // only set hovered when there is hovered child and nothing is
                // selected yet

                if (point && !selectedId) setHovered(point.childName);
              }}
              onDeactivated={([point]) => {
                if (point) setHovered(null, null, point.childName);
              }}
            />
          }
        >
          <VictoryAxis
            offsetY={chartPadding.bottom + 4}
            tickValues={ticks}
            tickFormat={stamp =>
              dateFormat(
                stamp,
                precision === Precision.YEARLY ? 'YYYY' : 'MMM YYYY',
              ).replace(' ', '\n')
            }
            style={{
              ...victoryTheme.commonAxis,
              tickLabels: {
                ...victoryTheme.commonAxis.tickLabels,
                textAnchor: datum =>
                  datum === head(ticks)
                    ? 'start'
                    : datum === last(ticks) ? 'end' : 'middle',
              },
            }}
          />
          <VictoryAxis
            crossAxis={false}
            domain={[0, getDataRange(chartData, 'y').max]}
            dependentAxis
            style={victoryTheme.commonAxis}
            tickCount={Math.round(
              (height - chartPadding.top - chartPadding.bottom) /
                tickLabelSpacing,
            )}
            offsetX={chartPadding.left}
          />
          {chartData.map(({ id, points, color, name }) => (
            <VictoryGroup key={id}>
              <VictoryLine
                data={points}
                name="line"
                style={{
                  data: {
                    stroke: color,
                    opacity: !selectedId || id === selectedId ? 1 : 0.3,
                    strokeWidth:
                      selectedId === id || hoveredId === id
                        ? strokeWidthActive
                        : strokeWidthNormal,
                  },
                }}
              />
              <VictoryScatter
                size={(datum, active) => (active ? 3 : 0)}
                data={points}
                name={id}
                style={{
                  data: {
                    fill: 'white',
                    stroke: color,
                    strokeWidth: 2,
                    cursor: 'pointer',
                  },
                }}
                events={[
                  {
                    target: 'data',
                    eventHandlers: {
                      onClick: () => {
                        setSelected(id);
                      },
                    },
                  },
                ]}
              />
            </VictoryGroup>
          ))}
        </VictoryChart>
        <div ref={el => (this.portalNode = el)} />
      </React.Fragment>
    );
  }
}

LineChart.propTypes = {
  width: number.isRequired,
  height: number.isRequired,
  chartData: arrayOf(
    shape({
      id: string.isRequired,
      points: arrayOf(
        shape({
          x: number.isRequired,
          y: number.isRequired,
          name: string,
          color: string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  precision: IPrecision,
  selectedId: string,
  hoveredId: string,
  setSelected: func,
  setHovered: func,
};

export default LineChart;



// WEBPACK FOOTER //
// ./src/components/LineChart/LineChart.jsx